import React, { useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { CHART_COLORS, formatDateLabel } from './chartUtils';
import { ChartTooltip } from './ChartTooltip';

/**
 * Componente de line chart compartido entre Data Explorer y Reportes.
 *
 * Props:
 *  - chartData: { labels: string[], series: Array<{name, label, period, data, formattedData}>, metrics?: Array<{name, is_default}> }
 *  - height: number (default 350)
 *  - colors: string[] (default CHART_COLORS)
 *  - showTooltip: boolean (default true)
 *  - showLegend: boolean (default true)
 *  - selectedMetrics: string[] | null — para filtrar series (usado por Data Explorer)
 */
/**
 * Genera un color más claro/transparente para las líneas de comparación.
 * Convierte hex a rgba con 50% de opacidad.
 */
function lightenColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.45)`;
}

export function SharedLineChart({
    chartData,
    height = 350,
    colors = CHART_COLORS,
    showTooltip = true,
    showLegend = true,
    selectedMetrics = null,
}) {
    const { labels, series, metrics } = chartData;

    // Filtrar series según métricas seleccionadas o defaults
    const filteredSeries = useMemo(() => {
        if (selectedMetrics && selectedMetrics.length > 0) {
            const ids = selectedMetrics.filter(m => m !== null);
            if (ids.length > 0) {
                return series.filter(s => ids.includes(s.name));
            }
        }
        if (metrics) {
            const defaults = metrics.filter(m => m.is_default).map(m => m.name);
            if (defaults.length > 0) {
                return series.filter(s => defaults.includes(s.name));
            }
        }
        return series;
    }, [series, metrics, selectedMetrics]);

    // Métricas únicas y dual Y-axis
    const uniqueMetrics = useMemo(() => [...new Set(filteredSeries.map(s => s.name))], [filteredSeries]);
    const hasDualAxis = uniqueMetrics.length === 2;

    // Colores por métrica, respetando orden de selectedMetrics si existe
    const metricColors = useMemo(() => {
        const map = {};
        if (selectedMetrics && selectedMetrics.length > 0) {
            selectedMetrics.filter(m => m !== null).forEach((id, i) => {
                if (uniqueMetrics.includes(id)) {
                    map[id] = colors[i % colors.length];
                }
            });
        }
        uniqueMetrics.forEach((name, i) => {
            if (!map[name]) {
                map[name] = colors[i % colors.length];
            }
        });
        return map;
    }, [uniqueMetrics, selectedMetrics, colors]);

    // Agrupar series por métrica para dual axis
    const seriesByMetric = useMemo(() => {
        if (!hasDualAxis) return null;
        const grouped = {};
        filteredSeries.forEach((serie, index) => {
            if (!grouped[serie.name]) grouped[serie.name] = [];
            grouped[serie.name].push({ ...serie, filteredIndex: index });
        });
        return grouped;
    }, [filteredSeries, hasDualAxis]);

    // Transformar a formato Recharts: [{label, series0, series1, ...}]
    const data = useMemo(() => {
        return labels.map((label, i) => {
            const point = { label };
            filteredSeries.forEach((serie, si) => {
                const key = `series${si}`;
                point[key] = serie.data[i];
                point[`${key}_name`] = serie.name;
                point[`${key}_label`] = serie.label;
                if (serie.formattedData && serie.formattedData[i] != null) {
                    point[`${key}_formatted`] = serie.formattedData[i];
                }
            });
            return point;
        });
    }, [labels, filteredSeries]);

    // Mapa de series para el tooltip
    const seriesMap = useMemo(() => {
        const map = {};
        filteredSeries.forEach((serie, i) => {
            map[`series${i}`] = {
                name: serie.name,
                label: serie.label,
                period: serie.period,
            };
        });
        return map;
    }, [filteredSeries]);

    if (filteredSeries.length === 0 || labels.length === 0) return null;

    // Determinar si hay series de comparación para agregar sufijo en la leyenda
    const hasComparison = filteredSeries.some(s => s.period === 'comparison');

    const getDisplayName = (serie) => {
        const baseName = serie.label.replace(/\s*-\s*Período\s+(Actual|Comparación)\s*$/i, '').trim();
        if (!hasComparison) return baseName;
        return serie.period === 'comparison' ? `${baseName} (ant.)` : baseName;
    };

    const getLineColor = (serie) => {
        const baseColor = metricColors[serie.name] || colors[0];
        return serie.period === 'comparison' ? lightenColor(baseColor) : baseColor;
    };

    const renderLines = () => {
        if (hasDualAxis && seriesByMetric && Object.keys(seriesByMetric).length === 2) {
            return Object.entries(seriesByMetric).map(([metricId, metricSeries], metricIndex) => {
                const yAxisId = metricIndex === 0 ? 'left' : 'right';
                return metricSeries.map((serie, si) => {
                    const dataKey = `series${serie.filteredIndex}`;
                    const displayName = getDisplayName(serie);
                    const lineColor = getLineColor(serie);
                    const strokeDasharray = serie.period === 'comparison' ? '5 5' : undefined;

                    return (
                        <Line
                            key={`${metricId}-${si}`}
                            type="monotone"
                            dataKey={dataKey}
                            name={displayName}
                            yAxisId={yAxisId}
                            stroke={lineColor}
                            strokeWidth={serie.period === 'comparison' ? 1.5 : 2}
                            strokeDasharray={strokeDasharray}
                            dot={{ r: serie.period === 'comparison' ? 3 : 4 }}
                            activeDot={{ r: serie.period === 'comparison' ? 4 : 6 }}
                            connectNulls={false}
                        />
                    );
                });
            }).flat();
        }

        return filteredSeries.map((serie, index) => {
            const dataKey = `series${index}`;
            const displayName = getDisplayName(serie);
            const lineColor = getLineColor(serie);
            const strokeDasharray = serie.period === 'comparison' ? '5 5' : undefined;

            return (
                <Line
                    key={index}
                    type="monotone"
                    dataKey={dataKey}
                    name={displayName}
                    stroke={lineColor}
                    strokeWidth={serie.period === 'comparison' ? 1.5 : 2}
                    strokeDasharray={strokeDasharray}
                    dot={{ r: serie.period === 'comparison' ? 3 : 4 }}
                    activeDot={{ r: serie.period === 'comparison' ? 4 : 6 }}
                    connectNulls={false}
                />
            );
        });
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 20, right: 0, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                    dataKey="label"
                    stroke="#666"
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatDateLabel}
                />
                {hasDualAxis ? (
                    <>
                        <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 12 }} width={40} orientation="left" />
                        <YAxis yAxisId="right" stroke="#666" tick={{ fontSize: 12 }} width={40} orientation="right" />
                    </>
                ) : (
                    <YAxis stroke="#666" tick={{ fontSize: 12 }} width={40} />
                )}
                {showTooltip && <Tooltip content={<ChartTooltip seriesMap={seriesMap} />} />}
                {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
                {renderLines()}
            </LineChart>
        </ResponsiveContainer>
    );
}
